/* ==========================================================================
   Lalla — header.js
   <sticky-header>  sticky modes (always / reveal on scroll up / static).
   <header-menu>    desktop dropdowns & mega menus on <details>: hover intent
                    for mouse users, one menu open at a time, Escape,
                    click outside and focus leaving the menu close it.
   ========================================================================== */

(() => {
  if (customElements.get('sticky-header')) return;

  const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this;
      this.mode = this.dataset.sticky || 'always';
      this.section.classList.toggle('header-static', this.mode === 'none');
      if (this.mode === 'none') return;

      this.lastY = window.scrollY;
      this.ticking = false;
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }

    update() {
      const y = Math.max(window.scrollY, 0);
      this.section.classList.toggle('is-scrolled', y > 4);
      if (this.mode === 'scroll-up') {
        const menuOpen = Boolean(this.querySelector('details[open]'));
        const locked = document.documentElement.classList.contains('scroll-locked');
        const goingDown = y > this.lastY;
        const pastHeader = y > this.offsetHeight * 2;
        this.section.classList.toggle('is-hidden', goingDown && pastHeader && !menuOpen && !locked);
      }
      this.lastY = y;
    }
  }

  customElements.define('sticky-header', StickyHeader);

  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.summary) return;

      this.addEventListener('mouseenter', () => {
        if (!hoverCapable.matches) return;
        clearTimeout(this.closeTimer);
        this.openTimer = setTimeout(() => this.open(true), 80);
      });

      this.addEventListener('mouseleave', () => {
        if (!hoverCapable.matches) return;
        clearTimeout(this.openTimer);
        this.closeTimer = setTimeout(() => this.close(), 200);
      });

      // A click on a menu opened by hover keeps it open instead of toggling it shut.
      this.summary.addEventListener('click', (event) => {
        if (this.openedByHover && this.details.open) {
          event.preventDefault();
          this.openedByHover = false;
        }
      });

      this.details.addEventListener('toggle', () => {
        if (!this.details.open) return;
        document.querySelectorAll('header-menu').forEach((menu) => {
          if (menu !== this) menu.close();
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.details.open) {
          event.preventDefault();
          this.close();
          this.summary.focus();
        }
      });

      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
      });
    }

    open(byHover = false) {
      if (this.details.open) return;
      this.openedByHover = byHover;
      this.details.open = true;
    }

    close() {
      this.openedByHover = false;
      if (this.details) this.details.open = false;
    }
  }

  customElements.define('header-menu', HeaderMenu);

  /* <announcement-bar>: one message at a time, previous/next buttons,
     optional auto-rotation paused on hover/focus and with reduced motion. */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messages = Array.from(this.querySelectorAll('[data-announcement-message]'));
      if (this.messages.length < 2) return;
      this.index = 0;
      this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.show(this.index - 1, true));
      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.show(this.index + 1, true));

      const delay = parseInt(this.dataset.autoplay, 10);
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!delay || reduced) return;
      this.paused = false;
      ['mouseenter', 'focusin'].forEach((type) => this.addEventListener(type, () => { this.paused = true; }));
      ['mouseleave', 'focusout'].forEach((type) => this.addEventListener(type, () => { this.paused = false; }));
      this.timer = setInterval(() => {
        if (!this.paused && !document.hidden) this.show(this.index + 1);
      }, delay);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    show(index, userInitiated = false) {
      this.index = (index + this.messages.length) % this.messages.length;
      this.messages.forEach((message, i) => {
        message.hidden = i !== this.index;
        message.classList.toggle('is-active', i === this.index);
      });
      // Manual navigation stops auto-rotation for good.
      if (userInitiated) clearInterval(this.timer);
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);

  document.addEventListener('click', (event) => {
    document.querySelectorAll('header-menu').forEach((menu) => {
      if (!menu.contains(event.target)) menu.close();
    });
  });
})();
